"use client";

import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import { useClientEnvironment } from "~/app/environment/client";
import { environmentOptions } from "~/queries/environment/environment.options";
import type { BookingActivity } from "~/types/api-types";

/** The env service's web config. Keyed on the app locale. */
const useEnv = () => {
  const { HANDSHAKE_ENV } = useClientEnvironment();
  const { locale } = useIntl();

  return useQuery(
    environmentOptions.queries.web({ handshake: HANDSHAKE_ENV, locale }),
  );
};

const useEnvActivityCategories = () => {
  const { HANDSHAKE_ENV } = useClientEnvironment();
  const { locale } = useIntl();

  return useQuery(
    environmentOptions.queries.activityCategories({
      handshake: HANDSHAKE_ENV,
      locale,
    }),
  );
};

/** Flattens the env service's booking activity categories into a single searchable list. */
const useBookingActivities = (): BookingActivity[] => {
  const { data: activityCategories = [] } = useEnvActivityCategories();

  return useMemo(
    () => activityCategories.flatMap((category) => category.activities),
    [activityCategories],
  );
};

export { useBookingActivities, useEnv, useEnvActivityCategories };
